import {
  Children,
  createElement,
  type CSSProperties,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';
import cx from 'clsx';
import IconCheckmarkRoundedFilled from '@repo/monochrome-icons/icon-checkmark-rounded-filled';
import IconCrossRoundedFilledSmall from '@repo/monochrome-icons/icon-cross-rounded-filled-small';
import IconInfoRoundedFilled from '@repo/monochrome-icons/icon-info-rounded-filled';
import IconPromoFilledSmall from '@repo/monochrome-icons/icon-promo-filled-small';
import IconWarningRoundedFilledSmall from '@repo/monochrome-icons/icon-warning-rounded-filled-small';
import styles from './disclaimer.module.css';

export interface DisclaimerProps {
  children: ReactNode;
  level?: 'info' | 'warning' | 'error' | 'promo' | 'promoBanner' | 'success';
  block?: boolean;
  className?: string;
  fullWidth?: boolean;
  style?: CSSProperties;
}

// TODO: change alt to translation when i18n
const ICONS = {
  info: { element: IconInfoRoundedFilled },
  warning: { element: IconWarningRoundedFilledSmall },
  error: { element: IconCrossRoundedFilledSmall },
  promo: { element: IconPromoFilledSmall },
  promoBanner: { element: IconPromoFilledSmall },
  success: { element: IconCheckmarkRoundedFilled },
};

function DisclaimerComponent({
  children,
  level = 'info',
  block = false,
  className,
  fullWidth = false,
  ...props
}: DisclaimerProps): ReactElement {
  const hasBlockContent = Children.toArray(children).some(
    child =>
      isValidElement(child) &&
      typeof child.type === 'string' &&
      ['div', 'p', 'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(child.type)
  );

  const MessageTag = hasBlockContent ? 'div' : 'p';

  return (
    <div
      className={cx(
        styles.wrapper,
        block && styles.block,
        !block && styles.inline,
        block && styles[`${level}Block`],
        fullWidth && styles.fullWidth,
        className
      )}
      data-test-level={level}
      {...props}
    >
      <div className={cx(styles.icon, styles[`${level}Icon`])}>
        {createElement(ICONS[level].element, {
          'aria-hidden': 'true',
        })}
      </div>
      <MessageTag className={cx('body-standard', styles.message)} data-test-message>
        {children}
      </MessageTag>
    </div>
  );
}

function DisclaimerInline({
  level = 'info',
  className,
  children,
  ...props
}: DisclaimerProps): ReactElement {
  return (
    <DisclaimerComponent
      block={false}
      className={className}
      data-test-disclaimer-inline
      data-testid="disclaimer-inline"
      level={level}
      {...props}
    >
      {children}
    </DisclaimerComponent>
  );
}

function DisclaimerBlock({
  level = 'info',
  className,
  children,
  ...props
}: DisclaimerProps): ReactElement {
  return (
    <DisclaimerComponent
      block
      className={className}
      data-test-disclaimer-block
      data-testid="disclaimer-block"
      level={level}
      {...props}
    >
      {children}
    </DisclaimerComponent>
  );
}

export const Disclaimer = {
  Inline: DisclaimerInline,
  Block: DisclaimerBlock,
};
